import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { gridStringOrNumberComparator } from "@mui/x-data-grid-pro";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  pictureTypeLabel,
  realEstateTypeLabel,
  searchConditionLabel,
} from "../labels";
import {
  type AiStoredPictureResult,
  type AiStoredPictureSearch,
} from "../types";

interface AiStoredPicturesResultsDialogProps {
  open: boolean;
  search: AiStoredPictureSearch;
  selectedIds: Set<number>;
  isReadOnly: boolean;
  onSelectedIdsChange: (ids: Set<number>) => void;
  onClose: () => void;
  onContinue: () => void;
}

const RESULTS_PER_PAGE = 20;
const LATEST_OWNER_ANNOTATION_TEXT =
  "最新の登記では所有者が異なる可能性があります";

const LatestOwnerAnnotationBadge = (): JSX.Element => (
  <Tooltip title={LATEST_OWNER_ANNOTATION_TEXT}>
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        fontSize: "0.75rem",
        borderRadius: "999px",
        border: "2px solid",
        borderColor: "warning.main",
        backgroundColor: "transparent",
        color: "warning.main",
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      所有者変更の可能性
    </Box>
  </Tooltip>
);

type SortField =
  | "isLatestOwner"
  | "realEstateType"
  | "pictureType"
  | "prefectureName"
  | "location"
  | "chibanKaoku"
  | "receivedDate"
  | "acquisitionType"
  | "lastUserAcquisitionDate";

type SortDirection = "asc" | "desc";

const latestOwnerAnnotationValue = (result: AiStoredPictureResult): string =>
  result.isLatestOwner === false ? "所有者変更の可能性" : "";

const acquisitionTypeLabel = (result: AiStoredPictureResult): string => {
  if (!result.isDownloadable) {
    return "取得不可";
  }
  return result.isChargeRequired ? "未取得・有料" : "取得済み・無料";
};

const compareGridStrings = gridStringOrNumberComparator as (
  left: string,
  right: string
) => number;

const compareResults = (
  left: AiStoredPictureResult,
  right: AiStoredPictureResult,
  field: SortField
): number => {
  if (field === "isLatestOwner") {
    return compareGridStrings(
      latestOwnerAnnotationValue(left),
      latestOwnerAnnotationValue(right)
    );
  }
  if (field === "acquisitionType") {
    return compareGridStrings(
      acquisitionTypeLabel(left),
      acquisitionTypeLabel(right)
    );
  }
  return String(left[field] ?? "").localeCompare(
    String(right[field] ?? ""),
    "ja",
    {
      numeric: true,
    }
  );
};

const displayDate = (value: string | null): string =>
  value === null ? "" : format(new Date(value), "yyyy/MM/dd");

export const AiStoredPicturesResultsDialog: React.FC<
  AiStoredPicturesResultsDialogProps
> = ({
  open,
  search,
  selectedIds,
  isReadOnly,
  onSelectedIdsChange,
  onClose,
  onContinue,
}) => {
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const selectableResults = search.results.filter(
    (result) => result.isDownloadable
  );
  const sortedResults = useMemo(() => {
    if (sortField === null) {
      return search.results;
    }
    const direction = sortDirection === "asc" ? 1 : -1;
    return [...search.results].sort(
      (left, right) => direction * compareResults(left, right, sortField)
    );
  }, [search.results, sortDirection, sortField]);
  const displayedResults = sortedResults.slice(
    page * RESULTS_PER_PAGE,
    (page + 1) * RESULTS_PER_PAGE
  );
  const allSelected =
    selectableResults.length > 0 &&
    selectableResults.every((result) =>
      selectedIds.has(result.pictureRequestDetailId)
    );
  const partiallySelected = !allSelected && selectedIds.size > 0;

  useEffect(() => {
    setPage(0);
  }, [search.results, sortDirection, sortField]);

  const changeSort = (field: SortField): void => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const sortableHeader = (label: string, field: SortField): React.ReactNode => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : "asc"}
      onClick={() => {
        changeSort(field);
      }}
    >
      {label}
    </TableSortLabel>
  );

  const toggleAll = (): void => {
    onSelectedIdsChange(
      allSelected
        ? new Set<number>()
        : new Set(
            selectableResults.map((result) => result.pictureRequestDetailId)
          )
    );
  };

  const toggleResult = (result: AiStoredPictureResult): void => {
    const next = new Set(selectedIds);
    if (next.has(result.pictureRequestDetailId)) {
      next.delete(result.pictureRequestDetailId);
    } else {
      next.add(result.pictureRequestDetailId);
    }
    onSelectedIdsChange(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: { borderRadius: 1, maxHeight: "88vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 1.5,
        }}
      >
        <Typography component="span" fontWeight={700}>
          検索結果 {search.returnedCount}件
        </Typography>
        <Typography component="span" variant="body2">
          {searchConditionLabel(search.query)}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 3 }}>
        {search.hasMore && search.warnings.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            条件に一致する登記がほかにも存在する可能性があります
          </Alert>
        )}
        {search.warnings.map((warning) => (
          <Alert key={warning} severity="warning" sx={{ mb: 1 }}>
            {warning}
          </Alert>
        ))}
        <TableContainer
          sx={{ maxHeight: 440, border: 1, borderColor: "divider" }}
        >
          <Table
            stickyHeader
            size="small"
            aria-label="AIサポート検索結果"
            sx={{ minWidth: 1680 }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    aria-label="取得可能な登記をすべて選択"
                    checked={allSelected}
                    indeterminate={partiallySelected}
                    disabled={isReadOnly || selectableResults.length === 0}
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>詳細画面</TableCell>
                <TableCell sx={{ minWidth: 180, textAlign: "center" }}>
                  {sortableHeader("注記", "isLatestOwner")}
                </TableCell>
                <TableCell sx={{ minWidth: 80, whiteSpace: "nowrap" }}>
                  {sortableHeader("種別", "realEstateType")}
                </TableCell>
                <TableCell sx={{ minWidth: 110, whiteSpace: "nowrap" }}>
                  {sortableHeader("事項種別", "pictureType")}
                </TableCell>
                <TableCell sx={{ minWidth: 100, whiteSpace: "nowrap" }}>
                  {sortableHeader("都道府県", "prefectureName")}
                </TableCell>
                <TableCell sx={{ minWidth: 240 }}>
                  {sortableHeader("所在", "location")}
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                  {sortableHeader("地番または家屋番号", "chibanKaoku")}
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  {sortableHeader("リユース登記", "receivedDate")}
                </TableCell>
                <TableCell sx={{ minWidth: 110 }}>
                  {sortableHeader("取得区分", "acquisitionType")}
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  {sortableHeader("取得済登記", "lastUserAcquisitionDate")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedResults.map((result) => {
                const selected = selectedIds.has(result.pictureRequestDetailId);
                return (
                  <TableRow
                    key={result.pictureRequestDetailId}
                    hover
                    selected={selected}
                    sx={{ opacity: result.isDownloadable ? 1 : 0.58 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        aria-label={`${result.location}を選択`}
                        checked={selected}
                        disabled={isReadOnly || !result.isDownloadable}
                        onChange={() => {
                          toggleResult(result);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {result.lastUserAcquisitionDate !== null ? (
                        <LinkButton
                          href={`/acquirebookstatus/${result.pictureRequestId}/`}
                          variant={ButtonVariantOption.Text}
                          size="small"
                          wrapperSize={SizingWrapperStyle.SMALL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          詳細を開く
                        </LinkButton>
                      ) : (
                        <Button size="small" disabled>
                          詳細を開く
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {result.isLatestOwner === false ? (
                        <LatestOwnerAnnotationBadge />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {realEstateTypeLabel(result.realEstateType)}
                    </TableCell>
                    <TableCell>
                      {pictureTypeLabel(result.pictureType)}
                    </TableCell>
                    <TableCell>{result.prefectureName}</TableCell>
                    <TableCell>{result.location}</TableCell>
                    <TableCell>{result.chibanKaoku ?? "-"}</TableCell>
                    <TableCell>
                      {result.receivedDate === result.lastUserAcquisitionDate
                        ? ""
                        : displayDate(result.receivedDate)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={acquisitionTypeLabel(result)}
                        color={
                          !result.isDownloadable
                            ? undefined
                            : result.isChargeRequired
                            ? "warning"
                            : "success"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {displayDate(result.lastUserAcquisitionDate)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {search.results.length > RESULTS_PER_PAGE && (
          <TablePagination
            component="div"
            count={search.results.length}
            page={page}
            rowsPerPage={RESULTS_PER_PAGE}
            rowsPerPageOptions={[RESULTS_PER_PAGE]}
            onPageChange={(_, newPage) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={() => undefined}
            labelRowsPerPage="ページあたりの行数:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from.toLocaleString()}〜${to.toLocaleString()} / ${count.toLocaleString()}`
            }
            sx={{ border: 1, borderTop: 0, borderColor: "divider" }}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          justifyContent: "space-between",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedIds.size}件を選択中（最大100件）
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose}>閉じる</Button>
          <ReadOnlyTooltip isReadOnly={isReadOnly}>
            <span>
              <Button
                variant="contained"
                startIcon={<DownloadOutlinedIcon />}
                disabled={isReadOnly || selectedIds.size === 0}
                onClick={onContinue}
              >
                選択した登記を取得
              </Button>
            </span>
          </ReadOnlyTooltip>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
